import { leagueRoute } from "@softmax/web-features/leagues";
import { buildHref } from "@softmax/web-addresses";
import { redirect } from "@tanstack/react-router";

import { watchLeagues } from "@/data/catalog";
import type { CreatorUploadData } from "../upload/creator-upload";
import { publicUploadData, readUpload } from "./data/creator-upload.data";
import { requireCreatorViewer } from "./data/creator-viewer";

export async function readSharedCoworld({
  signal,
  name,
  search,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly name: string;
  readonly search: string;
}): Promise<CreatorUploadData> {
  const parameters = new URLSearchParams(search);
  const id = parameters.get("version");
  const upload = id
    ? await readUpload({ signal, name, id, publicOnly: true })
    : null;

  if (upload?.game_address) {
    throw redirect({
      href: buildHref({
        name: "game.home",
        game: upload.game_address,
        search: parameters,
      }),
      statusCode: 307,
      headers: { "Cache-Control": "private, no-store" },
    });
  }

  if (!id) {
    const leagues = await watchLeagues({ signal });

    if (leagues === null)
      throw new Error("Unable to load public Coworld leagues");

    const matching = leagues.filter(
      (league) => league.game.coworld_name === name,
    );
    const league =
      matching.find((row) => row.id === row.game.default_league_id) ??
      matching[0];

    if (league) {
      throw redirect({
        href: buildHref({
          name: "game.home",
          game: leagueRoute(league).game,
          search: parameters,
        }),
        statusCode: 307,
        headers: { "Cache-Control": "private, no-store" },
      });
    }
  }

  if (upload) {
    return publicUploadData({
      upload,
      unlisted: false,
    });
  }

  const viewer = await requireCreatorViewer({
    signal,
    returnTo: buildHref({
      name: "coworldShared",
      coworldName: name,
      search: parameters,
    }),
  });

  const privateUpload: CreatorUploadData = {
    kind: "private",
    name,
    id,
    unlisted: false,
    viewer,
  };

  if (!id) return privateUpload;

  const owned = await readUpload({ signal, name, id, publicOnly: false });

  if (owned?.game_address) {
    throw redirect({
      href: buildHref({
        name: "game.home",
        game: owned.game_address,
        search: parameters,
      }),
      statusCode: 307,
      headers: { "Cache-Control": "private, no-store" },
    });
  }

  return privateUpload;
}
